import React, { useState, useEffect } from 'react'; // Import useEffect directly
import dayjs from 'dayjs';
// SVG Icons
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const MaximizeIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
const API_URL = '/api';
const SCREEN_NAME = 'CustomerMaster';
 
const CustomerViewModal = ({ open, onClose, customer }) => {
  const [loadingFile, setLoadingFile] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isMaximized, setIsMaximized] = useState(false);
  const [branches, setBranches] = useState([]); // Add state for branches
 
  // Department screen-config state
  const [screenConfig, setScreenConfig] = useState(null);
 
  // truthy helper + header visibility
  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };
  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };
 
  const fetchScreenConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) {}
        const msg = body?.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }
      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      setScreenConfig(cfg);
      return cfg;
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
      return null;
    }
  };
 
  // Fetch branches when modal opens - moved before the conditional return
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await fetch(`${API_URL}/branches`);
        const data = await response.json();
        setBranches(data);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };
 
    if (open && customer) {
      fetchBranches();
      // fetch department screen-config
      fetchScreenConfig();
    }
  }, [open, customer]);
 
  // Conditional check to prevent rendering if not open or customer data is missing
  if (!open || !customer) return null;
 
  // Helper function to get branch name from branch code
  const getBranchName = (branchCode) => {
    if (!branchCode) return '-';
    const branch = branches.find(b => b.BranchCode === branchCode);
    return branch ? `${branch.BranchCode} - ${branch.BranchName}` : branchCode;
  };
 
  const handleFileDownload = async (fileType) => {
    try {
      setLoadingFile(fileType);
      const response = await fetch(`${API_URL}/customers/${customer.customer_code}/files/${fileType}`);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${customer.customer_code}_${fileType}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Error downloading ${fileType}:`, error);
    } finally {
      setLoadingFile(null);
    }
  };
 
  const handleFileView = async (fileType) => {
    try {
      setLoadingFile(fileType);
      const response = await fetch(`${API_URL}/customers/${customer.customer_code}/files/${fileType}`);
      if (!response.ok) {
        throw new Error('Failed to view file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error(`Error viewing ${fileType}:`, error);
    } finally {
      setLoadingFile(null);
    }
  };
 
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
 
  const renderFilePreview = (fileType) => {
    if (!customer[fileType]) return '-';
 
    const isLoading = loadingFile === fileType;
    const isImage = fileType === 'logo';
 
    return (
      <div className="flex items-center gap-2">
        {isImage ? (
          <img
            src={`${API_URL}/customers/${customer.customer_code}/files/${fileType}`}
            alt="Company Logo"
            className="w-10 h-10 object-cover rounded-md"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM2NjY2NjYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiI+PC9yZWN0PjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ij48L2NpcmNsZT48cG9seWxpbmUgcG9pbnRzPSIyMSAxNSAxNSAxNSA5IDkgMyAxNSI+PC9wb2x5bGluZT48L3N2Zz4=';
            }}
          />
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="text-[#64748b] w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )}
        <button
          onClick={() => handleFileView(fileType)}
          className="p-2 text-[#A0AEC0] hover:text-[#384959] disabled:opacity-50"
          disabled={isLoading}
          title="View"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
        <button
          onClick={() => handleFileDownload(fileType)}
          className="p-2 text-[#A0AEC0] hover:text-[#384959] disabled:opacity-50"
          disabled={isLoading}
          title="Download"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>
        {isLoading && <span className="text-sm text-[#64748b]">Loading...</span>}
      </div>
    );
  };
 
  // Function to render contacts in a table format
  const renderContacts = (contacts) => {
    if (!contacts || contacts.length === 0) {
      return <div className="text-[#64748b]">No contacts available</div>;
    }
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Code</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contacts.map((contact, index) => (
              <tr key={index}>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{contact.contact_code || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{contact.first_name || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{contact.last_name || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{contact.email || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{contact.phone_number || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
 
  // Function to render addresses in a table format
  const renderAddresses = (addresses, type) => {
    if (!addresses || addresses.length === 0) {
      return <div className="text-[#64748b]">No {type} addresses available</div>;
    }
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address Code</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zip Code</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST No.</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PAN No.</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Person</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact No.</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {addresses.map((address, index) => (
              <tr key={index}>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.address_code || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.address_line1 || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.city || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.state || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.zip_code || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.country || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.gst_number || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.pan_number || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.contact_person_name || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{address.contact_person_number || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
 
  const sections = [
    {
      title: 'Customer Information',
      fields: [
        { label: 'Customer Code', value: customer.customer_code },
        { label: 'Customer Name', value: customer.customer_name },
 
        // Department (single field: Name ID Code inline) based on screen-config
        ...(shouldShowDepartmentOnHeader(screenConfig || customer?.screenConfig)
          ? [{
              label: 'Department',
              value: `$ ${customer.DepartmentCode || '-'}`,
            }]
          : []),
 
        { label: 'Country', value: customer.country },
        { label: 'Status', value: customer.status },
        { label: 'Customer Type', value: customer.customer_type },
        { label: 'Branch', value: getBranchName(customer.BranchCode) } // Add branch field
      ],
      name: 'Customer Info'
    },
    {
      title: 'Contact Information',
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="text-md font-medium text-[#384959] mb-3">Contacts</h4>
            {renderContacts(customer.contacts)}
          </div>
        </div>
      ),
      name: 'Contact Info'
    },
    {
      title: 'Address Information',
      content: (
        <div className="space-y-6">
          <div>
            <h4 className="text-md font-medium text-[#384959] mb-3">Billing Addresses</h4>
            {renderAddresses(customer.billing_addresses, 'billing')}
          </div>
          <div>
            <h4 className="text-md font-medium text-[#384959] mb-3">Shipping Addresses</h4>
            {renderAddresses(customer.shipping_addresses, 'shipping')}
          </div>
        </div>
      ),
      name: 'Address'
    },
    {
      title: 'Bank Information',
      fields: [
        { label: 'Account Number', value: customer.account_number || '-' },
        { label: 'Account Name', value: customer.account_name || '-' },
        { label: 'IFSC Code', value: customer.ifsc_code || '-' },
        { label: 'Bank Name', value: customer.bank_name || '-' },
        { label: 'Bank Branch', value: customer.bank_branch || '-' },
      ],
      name: 'Bank Info'
    },
    {
      title: 'Documents',
      fields: [
        {
          label: 'Company Logo',
          value: renderFilePreview('logo')
        },
        {
          label: 'GST Certificate',
          value: renderFilePreview('gst_certificate')
        },
        {
          label: 'Company Registration',
          value: renderFilePreview('company_reg_certificate')
        },
        {
          label: 'NDA Contract',
          value: renderFilePreview('nda_contract_file')
        }
      ],
      name: 'Documents'
    },
    {
      title: 'System Information',
      fields: [
        {
          label: 'Created At',
          value: customer.created_at ? dayjs(customer.created_at).format('YYYY-MM-DD HH:mm') : '-'
        },
        {
          label: 'Updated At',
          value: customer.updated_at ? dayjs(customer.updated_at).format('YYYY-MM-DD HH:mm') : '-'
        },
        { label: 'Created By', value: customer.created_by || '-' },
        { label: 'Updated By', value: customer.updated_by || '-' },
      ],
      name: 'System Info'
    }
  ];
 
  const handleNext = () => {
    if (currentStep < sections.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
 
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
 
  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
      ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none; /* Internet Explorer 10+ */
            scrollbar-width: none; /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none; /* Safari and Chrome */
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      {/* This is the actual slide-in panel with fixed header and scrollable content */}
      <div
        className={`bg-white shadow-2xl flex flex-col
          transition-transform transform-gpu duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
          ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : 'w-full max-w-6xl h-full rounded-none'}`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">Customer Details</h2>
            <div className="flex gap-2">
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
                onClick={onClose}
                title="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
       
        {/* Scrollable Content Area with all sections */}
        <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
          <div className="space-y-6 pb-6">
            {sections.map((section, index) => (
              <div key={index} className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0] uppercase">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">
                  {section.title}
                </h3>
               
                {/* Check if the section has custom content or fields */}
                {section.content ? (
                  section.content
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((field, fieldIndex) => (
                      <div key={fieldIndex} className="flex flex-col">
                        <span className="text-sm font-medium text-[#64748b]">
                          {field.label}
                        </span>
                        <span className="text-base text-[#384959] mt-1 font-semibold break-words whitespace-normal">
                          {field.label === 'Status' ? (
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm uppercase
                              ${field.value === 'Active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                              {field.value}
                            </span>
                          ) : (
                            field.value
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
       
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
              onClick={onClose}
            >
              Close
            </button>
            <button
              className="px-6 py-3 rounded-lg bg-[#6A89A7] text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 hover:bg-[#88BDF2]"
              onClick={onClose}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default CustomerViewModal;